import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';

import { MessageDeliveryStatus } from '@atlassian/ipm-choreographer/constants';
import type { NthUserContentType as NthUserContentTypeType } from '@atlassian/confluence-account-setup/types';
import { NthUserContentType } from '@atlassian/confluence-account-setup/types';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import { NTH_USER_SETUP_CONTENT_TYPE } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

export type OnboardingNudgeProps = {
	contentType: NthUserContentTypeType;
	contentId: string;
};

export const ContentType: typeof NthUserContentTypeType = NthUserContentType;

function isErrorHandled(error: Parameters<typeof markErrorAsHandled>[0]) {
	// Mark unauthorized error as handled and not report to monitoring client
	if (isUnauthorizedError(error) || error?.graphQLErrors?.[0]?.message === 'unauthorized') {
		markErrorAsHandled(error);
		return true;
	}
	return false;
}

const i18n = defineMessages({
	popularHeading: {
		id: 'onboarding-nth-user-setup-modal.content-type-spotlight.nth-user.content-types.popular.heading',
		defaultMessage:
			'{contentType, select, page {This is the most popular page in your site} whiteboard {This is the most popular whiteboard in your site} liveDoc {This is the most popular live doc in your site} database {This is the most popular database in your site} other {This content is popular on your site}}',
		description:
			'Heading for a floating nudge around a page, live doc, whiteboard, or database on a sample page of that content type',
	},
	nonPopularHeading: {
		id: 'onboarding-nth-user-setup-modal.content-type-spotlight.nth-user.content-types.non-popular.heading',
		defaultMessage:
			'{contentType, select, page {This is a sample page in your space} whiteboard {This is a sample whiteboard in your space} liveDoc {This is a sample live doc in your space} database {This is a page on how to use databases} other {This is an example of your selected content type in your space}}',
		description:
			'Heading for a floating nudge on a page, live doc, whiteboard, or database on a popular page of that content type',
	},
	contentDescription: {
		id: 'onboarding-nth-user-setup-modal.content-type-spotlight.nth-user.content-types.content.description',
		defaultMessage:
			'{contentType, select, page {Take a quick look at the page and learn how Confluence pages work.} whiteboard {Take a quick look at the whiteboard and learn how Confluence whiteboards work.} liveDoc {Take a quick look at the live doc and learn how Confluence live docs work.} database {Take a quick look at the database and learn how Confluence databases work.} databaseDefault {Take a quick look at the page and learn how Confluence databases work.} other {Take a quick look at the content and learn how Confluence content types work.}}',
		description:
			'Content description for a nudge around a page, live doc, whiteboard, or database highlighting the most popular content',
	},
	dismissAction: {
		id: 'onboarding-nth-user-setup-modal.content-type-spotlight.dismiss.action',
		defaultMessage: 'Dismiss',
		description: 'Action text for a button to dismiss a spotlight card',
	},
});

const styles = cssMap({
	fixedSpotlightWrapper: {
		position: 'fixed',
		zIndex: 800,
		right: token('space.1000'),
		top: token('space.800'),
	},
	// position the spotlight card relative to the fixed wrapper
	relativeSpotlightWrapper: {
		position: 'relative',
		top: token('space.1000'),
	},
	// chaining specific overrides for content types
	liveDocSpacing: {
		position: 'relative',
		top: token('space.800'),
	},
});

export const contentTypeMapping = (contentType: NthUserContentTypeType): { messageId: string } => {
	switch (contentType) {
		case NthUserContentType.PAGE:
			return { messageId: 'onboarding-nth-user-page-content-type-spotlight' };
		case NthUserContentType.LIVE_DOC:
			return { messageId: 'onboarding-nth-user-live-doc-content-type-spotlight' };
		case NthUserContentType.WHITEBOARD:
			return { messageId: 'onboarding-nth-user-whiteboard-content-type-spotlight' };
		case NthUserContentType.DATABASE:
			return { messageId: 'onboarding-nth-user-database-content-type-spotlight' };
		default:
			return { messageId: '' };
	}
};

const actionSubjectId = 'contentTypeNthUserOnboardingSpotlight';
const source = 'nthUserContentTypeScreen';

export const ContentTypeSpotlight = ({
	contentType,
	contentId,
}: OnboardingNudgeProps): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const { setOnboardingState } = useSetOnboardingState();
	const { SpotlightCard } = useChoreographerAssets();

	const [isLocalNudgeShowing, setIsLocalNudgeShowing] = useState(false);

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([
		NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_SELECTED_CONTENT_TYPE,
		NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_CONTENT_TYPE_SPOTLIGHT_SEEN,
		NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_CONTENT_TYPE_POPULAR_CONTENT_FOUND,
		NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_CONTENT_TYPE_LANDING_CONTENT_ID,
	]);

	const {
		nthUserContentTypeSpotlightSeen,
		nthUserContentTypePopularContentFound,
		nthUserSelectedContentType,
		nthUserContentTypeLandingContentId,
	} = deserializeState(onboardingStateData);

	const isOnboardingStateLoaded = Boolean(
		!onboardingStateLoading && !onboardingStateError && onboardingStateData,
	);
	const isEligibleForNudge =
		isOnboardingStateLoaded &&
		!nthUserContentTypeSpotlightSeen &&
		Boolean(contentTypeMapping(contentType).messageId);

	const isDefaultDatabaseContent =
		nthUserSelectedContentType === NthUserContentType.DATABASE &&
		contentType === NthUserContentType.PAGE;
	const isTargetContent =
		(contentType === nthUserSelectedContentType || isDefaultDatabaseContent) &&
		// if there is popular content, ensure the content ids match
		contentId === nthUserContentTypeLandingContentId;

	const hasMetAllSpotlightConditions = isEligibleForNudge && isTargetContent;

	const choreographerMessageId = contentTypeMapping(contentType).messageId;

	useEffect(() => {
		if (hasMetAllSpotlightConditions) {
			setIsLocalNudgeShowing(true);
		}
	}, [hasMetAllSpotlightConditions]);

	const handleOnDismiss = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'button',
				actionSubjectId,
				source,
				attributes: {
					contentType,
					isPopularContent: nthUserContentTypePopularContentFound ?? false,
				},
			},
		}).fire();

		setIsLocalNudgeShowing(false);
	};

	const headingMessage = nthUserContentTypePopularContentFound
		? i18n.popularHeading
		: i18n.nonPopularHeading;

	const getLocalizedSelectOptionString = (isHeading: boolean = true) => {
		if (nthUserSelectedContentType === NthUserContentType.LIVE_DOC) {
			// Select values only accept alphanumeric or underscores characters
			return 'liveDoc';
		}

		if (!nthUserContentTypePopularContentFound && isDefaultDatabaseContent) {
			// the spotlight for database default content shows up on a page
			// the non-popular database heading already adjusts for the database default content
			return isHeading ? NthUserContentType.DATABASE : 'databaseDefault';
		}

		return contentType;
	};

	const onMessageDispositionCallback = useCallback(
		(message: string) => {
			if (message === MessageDeliveryStatus.STARTED) {
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: actionSubjectId,
						source,
						attributes: {
							contentType,
							isPopularContent: nthUserContentTypePopularContentFound ?? false,
						},
					},
				}).fire();

				if (!nthUserContentTypeSpotlightSeen) {
					setOnboardingState({
						key: NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_CONTENT_TYPE_SPOTLIGHT_SEEN,
						value: 'true',
					});
				}
			}
		},
		[
			createAnalyticsEvent,
			contentType,
			nthUserContentTypePopularContentFound,
			nthUserContentTypeSpotlightSeen,
			setOnboardingState,
		],
	);

	if (onboardingStateError && !isErrorHandled(onboardingStateError)) {
		return (
			<>
				<ErrorDisplay error={onboardingStateError} />
			</>
		);
	}

	if (!isLocalNudgeShowing) {
		return null;
	}

	return (
		<>
			<Box xcss={styles.fixedSpotlightWrapper}>
				<Box xcss={styles.relativeSpotlightWrapper}>
					<Box xcss={cx(contentType === NthUserContentType.LIVE_DOC && styles.liveDocSpacing)}>
						<SpotlightCard
							messageId={choreographerMessageId}
							messageType="engagement"
							width={275}
							heading={intl.formatMessage(headingMessage, {
								contentType: getLocalizedSelectOptionString(),
							})}
							actions={[
								{
									onClick: handleOnDismiss,
									text: intl.formatMessage(i18n.dismissAction),
								},
							]}
							onMessageDisposition={onMessageDispositionCallback}
						>
							<FormattedMessage
								{...i18n.contentDescription}
								values={{
									contentType: getLocalizedSelectOptionString(false),
								}}
							/>
						</SpotlightCard>
					</Box>
				</Box>
			</Box>
		</>
	);
};

export const OnboardingNthUserContentTypeSpotlight = ({
	contentType,
	contentId,
}: OnboardingNudgeProps): React.JSX.Element => {
	return (
		<GenericErrorBoundary
			attribution={Attribution.CE_MAU}
			renderOnError={() => {
				return null;
			}}
		>
			<ContentTypeSpotlight contentType={contentType} contentId={contentId} />
		</GenericErrorBoundary>
	);
};
